import moment from 'moment'

const EXPIRING_WINDOW_DAYS = 30

export interface ExpiringProductInfo {
  name: string
  expirationDate: string
}

export interface ProductWithExpiration {
  name: string
  expirationDate?: string | null
  expiration_date?: string | null
  hasRenewableSubscription?: boolean
  has_renewable_subscription?: boolean
}

export function formatExpirationDate(date: string): string {
  return moment(date).format('MMM. Do, YYYY')
}

export function buildExpiringTooltipText(
  product: ExpiringProductInfo,
  copy: Record<string, string> = {},
): string {
  const template =
    copy.singleProductDescription ||
    '{productName} access expires on {expirationDate}. Renew or update your subscription to keep uninterrupted access.'
  return template
    .replace('{productName}', product.name)
    .replace('{expirationDate}', formatExpirationDate(product.expirationDate))
}

/** Matches production dashboard: an expired product opens the subscription flow instead of launching. */
export function isProductExpired(
  product: Pick<ProductWithExpiration, 'expirationDate' | 'expiration_date'>,
  now: Date = new Date(),
): boolean {
  const expirationRaw = product.expirationDate ?? product.expiration_date
  if (!expirationRaw) return false
  const expiration = moment(expirationRaw)
  return expiration.isValid() && expiration.isSameOrBefore(now)
}

/** Matches production dashboard: expiring within 30 days, not expired, no auto-renewal. */
export function isProductExpiring(
  product: ProductWithExpiration,
  now: Date = new Date(),
): boolean {
  const expirationRaw = product.expirationDate ?? product.expiration_date
  if (!expirationRaw) return false

  const hasRenewable =
    product.hasRenewableSubscription ?? product.has_renewable_subscription
  if (hasRenewable) return false

  // Parsed the same way formatExpirationDate displays it (local time) and compared by day,
  // so a product stays "expiring" through the whole date the banner shows.
  const expiration = moment(expirationRaw)
  if (!expiration.isValid() || expiration.isBefore(now, 'day')) return false

  const windowEnd = moment(now).add(EXPIRING_WINDOW_DAYS, 'days')
  return expiration.isSameOrBefore(windowEnd, 'day')
}

export function getExpiringProducts(
  products: ProductWithExpiration[],
  now: Date = new Date(),
): ExpiringProductInfo[] {
  return products
    .filter((p) => isProductExpiring(p, now))
    .map((p) => ({
      name: p.name,
      expirationDate: (p.expirationDate ?? p.expiration_date)!,
    }))
    .sort((a, b) => moment(a.expirationDate).valueOf() - moment(b.expirationDate).valueOf())
}
